import {
  NotificationVariants,
  QuoteStatusEnum,
  logger,
  type ExecutionReport,
  type IWebSocketClient,
  type Quote,
  type Security,
  type SideEnum,
  type SubscriptionResponse,
  type ToastProps,
} from '@talos/kyoko';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Observable } from 'rxjs';

import { acceptQuote, quoteToAcceptQuoteParams } from '../../../../components/OMS/NewRFQ/acceptQuote';
import { shouldDeArmTradeBest } from '../../../../components/OMS/NewRFQ/utils';
import { getOrdTypeForQuote, type QuoteMarketWithBestBidOffer } from '../../../../utils/rfq';
import { getQuoteTradeSidePrice } from '../utils';

const ACCEPT_BUTTON_STATE = {
  Idle: 'idle',
  Armed: 'armed',
  Loading: 'loading',
} as const;
type AcceptButtonState = (typeof ACCEPT_BUTTON_STATE)[keyof typeof ACCEPT_BUTTON_STATE];

export { ACCEPT_BUTTON_STATE };

export interface UseQuoteAcceptButtonParams {
  /** The full Quote entity */
  quote: Quote;
  /** Which side this button trades */
  side: SideEnum;
  /** The pre-computed best market for this side (bid for Sell, offer for Buy) */
  bestMarket: QuoteMarketWithBestBidOffer | undefined;
  /** Resolved security for the quote's symbol */
  security: Security | undefined;
  /** Allowed slippage percentage value (e.g. "0.0002" for 2 bps) */
  allowedSlippage?: string;
  /** Externally computed disabled state (role auth, side restrictions, etc.) */
  disabled: boolean;
  /** When true, remove '-' from CareOrderID for PostTradeClearingID */
  truncateCareOrderID: boolean;
  /** WebSocket client for sending the accept request */
  webSocketClient: IWebSocketClient<unknown>;
  /** Live execution report stream from ExecutionReportsProvider */
  executionReportsSub: Observable<SubscriptionResponse<ExecutionReport>>;
  /** Toast dispatch function for surfacing errors to the user */
  addToast: (toast: ToastProps) => void;
  /** Whether the session is in mobile layout (for RequestSource tagging) */
  isMobileLayout?: boolean;
}

interface UseQuoteAcceptButtonReturn {
  /** Current state machine state */
  state: AcceptButtonState;
  /** Click handler to attach to the button */
  onClick: () => void;
  /** Reset handler (e.g., for blur / mouse leave) */
  onReset: () => void;
}

/**
 * Manages the full double-click-to-trade lifecycle for a quote accept button.
 *
 * State machine:
 * - idle -> armed: first click (arms current best market)
 * - armed -> loading: second click (sends acceptQuote via websocket)
 * - armed -> idle: best market changes (RFQ ordType only), onReset called, or quote leaves Open state
 * - loading -> idle: quote reaches a terminal status, or the publication returns an error
 */
export function useQuoteAcceptButtonState({
  quote,
  side,
  bestMarket,
  security,
  allowedSlippage,
  disabled,
  truncateCareOrderID,
  webSocketClient,
  executionReportsSub,
  addToast,
  isMobileLayout = false,
}: UseQuoteAcceptButtonParams): UseQuoteAcceptButtonReturn {
  const [state, setState] = useState<AcceptButtonState>(ACCEPT_BUTTON_STATE.Idle);
  const armedMarketRef = useRef<string | undefined>(undefined);

  const currentPrice = getQuoteTradeSidePrice(bestMarket, side);
  const currentBestMarket = bestMarket?.Market;
  const ordType = getOrdTypeForQuote(security, quote.PricingMode, quote.RFQTradingProtocol);
  const quoteStatus = quote.QuoteStatus;

  // Reset to idle when the quote leaves the Open state (handles both armed and loading)
  useEffect(() => {
    if (quoteStatus !== QuoteStatusEnum.Open) {
      setState(ACCEPT_BUTTON_STATE.Idle);
      armedMarketRef.current = undefined;
    }
  }, [quoteStatus]);

  // Reset to idle when the best market changes while armed (RFQ ordType only)
  useEffect(() => {
    if (
      state === ACCEPT_BUTTON_STATE.Armed &&
      shouldDeArmTradeBest({
        ordType,
        armedMarket: armedMarketRef.current,
        currentBestMarket,
      })
    ) {
      setState(ACCEPT_BUTTON_STATE.Idle);
      armedMarketRef.current = undefined;
    }
  }, [state, ordType, currentBestMarket]);

  // Stable function to reset from loading (uses functional updater to avoid stale closures)
  const resetFromLoading = useCallback(() => {
    setState(prev => {
      if (prev === ACCEPT_BUTTON_STATE.Loading) {
        armedMarketRef.current = undefined;
        return ACCEPT_BUTTON_STATE.Idle;
      }
      return prev;
    });
  }, []);

  const onClick = useCallback(() => {
    if (disabled || !security || !bestMarket) {
      return;
    }

    if (state === ACCEPT_BUTTON_STATE.Idle) {
      if (currentPrice == null) {
        return;
      }
      armedMarketRef.current = currentBestMarket;
      setState(ACCEPT_BUTTON_STATE.Armed);
      return;
    }

    if (state === ACCEPT_BUTTON_STATE.Armed) {
      if (currentPrice == null) {
        setState(ACCEPT_BUTTON_STATE.Idle);
        armedMarketRef.current = undefined;
        return;
      }

      setState(ACCEPT_BUTTON_STATE.Loading);

      const params = quoteToAcceptQuoteParams({
        quote,
        childQuote: bestMarket,
        side,
        price: currentPrice,
        allowedSlippage,
        truncateCareOrderID,
        security,
        associatedCareOrder: quote.careOrder,
        tradeBest: true,
      });

      if (!params) {
        logger.error(new Error('Failed to build accept quote params'), {
          extra: { RFQID: quote.RFQID, Symbol: quote.Symbol },
        });
        resetFromLoading();
        addToast({
          text: 'An error occurred while accepting the quote. Please try again.',
          variant: NotificationVariants.Negative,
          dismissable: true,
          timeout: 5000,
        });
        return;
      }

      const handleAcceptQuoteFailure = (result: { text: string; variant?: NotificationVariants }) => {
        resetFromLoading();
        logger.error(new Error('acceptQuote failed'), {
          extra: { RFQID: quote.RFQID, Symbol: quote.Symbol, side },
        });
        addToast({
          text: result.text,
          variant: result.variant ?? NotificationVariants.Negative,
          dismissable: true,
          timeout: 5000,
        });
      };

      void acceptQuote(params, {
        webSocketClient,
        executionReportsSub,
        isMobileLayout,
      })
        .then(resetFromLoading)
        .catch(handleAcceptQuoteFailure);
    }
  }, [
    disabled,
    state,
    currentPrice,
    currentBestMarket,
    quote,
    side,
    bestMarket,
    security,
    allowedSlippage,
    truncateCareOrderID,
    webSocketClient,
    executionReportsSub,
    addToast,
    resetFromLoading,
    isMobileLayout,
  ]);

  const onReset = useCallback(() => {
    if (state === ACCEPT_BUTTON_STATE.Armed) {
      setState(ACCEPT_BUTTON_STATE.Idle);
      armedMarketRef.current = undefined;
    }
  }, [state]);

  return { state, onClick, onReset };
}
